/**
 * Muting a chat: what the options mean and whether a stored mute is still in force.
 *
 * Pure, so the expiry arithmetic is testable without a clock or a database. The hook in
 * src/hooks/useChatMute.ts owns the rows; the fan-out trigger applies the same rule in
 * SQL (20260910130000_chat_mutes.sql).
 */

export type MuteDuration = '8h' | '1w' | 'forever';

export const MUTE_DURATIONS: { value: MuteDuration; label: string }[] = [
  { value: '8h', label: 'For 8 hours' },
  { value: '1w', label: 'For 1 week' },
  { value: 'forever', label: 'Until I turn it back on' },
];

const EIGHT_HOURS_MS = 8 * 60 * 60 * 1000;
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * When a mute of this length should lapse, as an ISO string, or null for "until I turn
 * it back on" -- which is stored as a NULL muted_until rather than a far-future date, so
 * nothing ever silently un-mutes.
 */
export function muteExpiry(duration: MuteDuration, now: Date = new Date()): string | null {
  switch (duration) {
    case '8h':
      return new Date(now.getTime() + EIGHT_HOURS_MS).toISOString();
    case '1w':
      return new Date(now.getTime() + ONE_WEEK_MS).toISOString();
    case 'forever':
      return null;
  }
}

/**
 * Is a stored mute still in force?
 *
 * Expired rows are left in the table on purpose -- they cost nothing and are overwritten
 * the next time the thread is muted -- so this is what decides, not the row's existence.
 */
export function isMuteActive(
  mutedUntil: string | null | undefined,
  now: Date = new Date()
): boolean {
  if (mutedUntil == null) return true;
  const until = new Date(mutedUntil).getTime();
  // An unparseable timestamp is treated as a permanent mute rather than silently
  // un-muting: the user asked for quiet, and the safe failure is to stay quiet.
  if (Number.isNaN(until)) return true;
  return until > now.getTime();
}

/** "Muted until 14:30" / "Muted" -- for the menu, so the choice is not a mystery. */
export function describeMute(
  mutedUntil: string | null | undefined,
  now: Date = new Date()
): string {
  if (mutedUntil == null) return 'Muted';
  const until = new Date(mutedUntil);
  if (Number.isNaN(until.getTime())) return 'Muted';

  const sameDay = until.toDateString() === now.toDateString();
  return sameDay
    ? `Muted until ${until.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`
    : `Muted until ${until.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
}
